import { Component, HostListener } from '@angular/core';
import { PopoverController, ModalController, NavController } from '@ionic/angular';
import { HeaderSettingPopover } from '../header-setting-popover/header-setting-popover';
import * as moment from 'moment';
import { ShiftSelectorModal } from '../shift-selector/shift-selector-modal';
import { ShiftProvider } from '../../services/shift-provider';
import { UnifiedLoginService } from '@monorepo/services';
import { AnalyticsConfigCacheService } from '../../services/analytics-config-cache.service';
import { select, Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { IAppState, selectHeaderInfo } from '../../state';
import { ToggleMobileNav } from '../../state/header/header.actions';
import { THeaderResponse, TShiftDtoResponse } from '@monorepo/types';
import { LOCAL_STORAGE_UTILS } from '@monorepo/utils';
import { ChatbotService } from '../../services/ai-analytics.service';

@Component({
  selector: 'common-header',
  templateUrl: `common-header.html`,
  styleUrls: ['./common-header.scss'],
})
export class CommonHeader {
  public onLine = true;
  public currentTime = moment;
  private BREAK_POINT = 992;

  public isMobileView = window.innerWidth < this.BREAK_POINT;

  iconClassesLarge = ['zf-icon--size-l', 'zf-icon--margin-none'];
  iconClassesSmall = ['zf-icon--size-xs', 'zf-icon--margin-none'];
  logoClasses = ['zf-icon--size-logo', 'zf-icon--margin-none'];

  public shiftDisplayName!: string;
  public shiftList: TShiftDtoResponse = [];

  public selectedHeaderInfo$: Observable<THeaderResponse>;

  constructor(
    public popoverController: PopoverController,
    public modalController: ModalController,
    private shiftProvider: ShiftProvider,
    public navController: NavController,
    public store: Store<IAppState>,
    public unifiedLoginService: UnifiedLoginService,
    private aiAnalyticsService: ChatbotService,
    private analyticsConfigCache: AnalyticsConfigCacheService,
  ) {
    this.setShiftProperties();
    this.selectedHeaderInfo$ = this.store.pipe(select(selectHeaderInfo));
  }

  private setShiftProperties() {
    const activeShift = this.shiftProvider.getActiveShift();
    if (activeShift) {
      this.shiftDisplayName = activeShift.shift_display_name;
      this.shiftList = this.shiftProvider.getShifts();
    }
  }

  public async presentPopover(event: any, languageInfo: THeaderResponse['language']) {
    const defaultLanguageCode = LOCAL_STORAGE_UTILS.retrieveData('locale');
    const defaultLanguage = languageInfo.find((lang) => lang.languageCode === defaultLanguageCode);
    const popover = await this.popoverController.create({
      event,
      component: HeaderSettingPopover,
      componentProps: {
        currentLanguage: defaultLanguage,
        languageOptions: languageInfo,
      },
      cssClass: 'header-popover',
    });

    await popover.present();

    const action = await popover.onDidDismiss();
    if (action['data'] === 'LOGOUT') {
      await this.analyticsConfigCache.clearCache();
      this.unifiedLoginService.logoutWithRedirect();
    }
  }

  public shiftSelectorClick() {
    if (this.shiftProvider.isMultiShiftConfigured) {
      const activeShiftIndex = this.shiftProvider.getActiveShiftIndex();

      const transformedShifts = this.shiftList.map((shift) => {
        return [
          {
            value: shift.shift_display_name,
          },
        ];
      });

      const shiftModalPromise = this.modalController.create({
        component: ShiftSelectorModal,
        componentProps: { items: transformedShifts, activeIndex: activeShiftIndex },
        cssClass: 'zf-shift-selector',
      });

      shiftModalPromise.then((shiftModal) => {
        shiftModal.present().then((a) => {
          shiftModal.onDidDismiss().then((d) => {
            // Handle shift change
            const index = d.data;
            if (index !== null || index === 0) {
              this.shiftProvider.changeActiveShift(index);
              this.setShiftProperties();
            }
          });
        });
      });
    }
  }

  openPage({ route }) {
    this.navController.navigateRoot(route, {
      ...(route === '/page-analytics/redirect' && { skipLocationChange: true }),
    });
  }

  toggleOrgHierachy() {
    this.store.dispatch(new ToggleMobileNav());
  }

  @HostListener('window:online')
  handleOnline() {
    this.onLine = true;
  }

  @HostListener('window:offline')
  handleOffline() {
    this.onLine = false;
  }

  @HostListener('window:resize', [])
  onResize() {
    this.isMobileView = window.innerWidth < this.BREAK_POINT;
  }

  async openAiAnalytics() {
    await this.aiAnalyticsService.openAiAnalytics();
  }
}
